'use client';

import { useState } from 'react';

import { motion } from 'framer-motion';

import { Card } from '@/components/Default/Card';
import { ContentView } from '@/components/Default/Section/ContentView';
import { Description } from '@/components/Default/Title/Description';
import { Header } from '@/components/Default/Title/Header';
import { Label } from '@/components/Default/Title/Label';
import { Section } from '@/components/Default/Section/Section';
import { Title } from '@/components/Default/Title/Title';

export interface Product {
    title: string;
    description: string;
    src: string;
    thumbnail: string;
    href: string;
    available?: boolean;
}

const Products = () => {
    const [isInViewport, setInViewport] = useState<boolean>(false);

    const products: Product[] = [
        {
            'title': 'Groepenkast vervangen',
            'description': 'Hier vind je meer informatie over het vervangen van groepenkasten door Time for Solar.',
            'src': '/assets/icons/fusebox.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/fusebox.webp',
            'href': '/diensten/groepenkast-vervangen',
            'available': true
        },
        {
            'title': 'Kookgroep installatie',
            'description': 'Hier vind je meer informatie over de installatie van kookgroepen door Time for Solar.',
            'src': '/assets/icons/cooktop.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/stovetop.webp',
            'href': '/diensten/kookgroep-installatie',
            'available': true
        },
        {
            'title': 'Storingsdienst 24/7',
            'description': 'Hier vind je meer informatie over de 24/7 storingsdienst van Time for Solar.',
            'src': '/assets/icons/emergency.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/emergency.webp',
            'href': '/diensten/storingsdienst',
            'available': true
        },
        {
            'title': 'Elektra renovatie',
            'description': 'Hier vind je meer informatie over elektra renovatie door Time for Solar.',
            'src': '/assets/icons/electrical-renovation.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/electric-renovation.webp',
            'href': '/diensten/elektra-renovatie',
            'available': true
        },
        {
            'title': '3 fase aansluiting',
            'description': 'Hier vind je meer informatie over de 3 fase aansluiting die Time for Solar kan verzorgen.',
            'src': '/assets/icons/three-phase.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/3phase.webp',
            'href': '/diensten/3-fase-aansluiting',
            'available': true
        },
        {
            'title': 'Elektra aanleggen',
            'description': 'Hier vind je meer informatie over het aanleggen van elektra door Time for Solar.',
            'src': '/assets/icons/connect.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/electric-expansion.webp',
            'href': '/diensten/elektra-aanleggen',
            'available': true
        },
        {
            'title': 'Zonnepanelen installeren',
            'description': 'Hier vind je meer informatie over de installatie van zonnepanelen door Time for Solar.',
            'src': '/assets/icons/solar-panels.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/solar_panels.webp',
            'href': '/producten/zonnepanelen',
            'available': true
        },
        {
            'title': 'Laadpaal installeren',
            'description': 'Hier vind je meer informatie over het installeren van laadpalen door Time for Solar.',
            'src': '/assets/icons/charging-station.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/charging_station.webp',
            'href': '/diensten/laadpaal-installeren',
            'available': true
        },
        {
            'title': 'Airco installeren',
            'description': 'Hier vind je meer informatie over het installeren van airconditioning door Time for Solar.',
            'src': '/assets/icons/airco.png',
            'thumbnail': 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/airco.webp',
            'href': '/producten/airco',
            'available': true
        }
    ];

    return (
        <Section className='bg-gray-100 my-0' onInViewport={(inViewport: boolean) => setInViewport(inViewport)} inViewOptions={{ once: true }}>
            <ContentView>
                <Title className='w-full max-w-screen-md mx-0'>
                    <Label>
                        <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth='1.5' stroke='currentColor' className='w-6 h-6'>
                            <path strokeLinecap='round' strokeLinejoin='round' d='M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18' />
                        </svg>
                        Complete oplossingen voor al uw elektra-behoeften
                    </Label>
                    <Header align='left'>
                        Onze dagelijkse diensten
                    </Header>
                    <Description>
                        Voor al uw elektra-klussen kunt u bij ons terecht. Of het nu gaat om een storing, het installeren van stopcontacten, of het vervangen van uw groepenkast, wij staan voor u klaar.
                    </Description>
                </Title>
            </ContentView>
            <ContentView className='px-4'>
                <div className='w-fit grid grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-8 mx-auto'>
                    {products.map((product: Product, idx: number) => {
                        return (
                            <motion.div
                                key={idx}
                                initial={{
                                    opacity: 0,
                                    y: 150
                                }}
                                animate={{
                                    opacity: isInViewport ? 1 : 0,
                                    y: isInViewport ? 0 : 150
                                }}
                                transition={{
                                    ease: 'easeOut',
                                    delay: 0.10 * idx
                                }}
                            >
                                <Card {...product} />
                            </motion.div>
                        );
                    })}
                </div>
            </ContentView>
        </Section>
    );
};

export default Products;